import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  BarChart, Bar, XAxis, YAxis, Tooltip,
  LineChart, Line, Legend, Area, AreaChart
} from 'recharts';
import { 
  ClipboardCheck, 
  CheckCircle2, 
  AlertCircle, 
  Building2, 
  TrendingUp,
  Calendar,
  ChevronRight,
  Plus,
  Heart,
  MessageCircle,
  Lightbulb,
  Star,
  BookOpen,
  Users,
  Clock,
  Zap,
  Target,
  Award,
  Upload,
  Calculator,
  Trophy
} from 'lucide-react';
import { toast } from 'sonner';
import { BRAND_PHOTOS } from '../lib/brand';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A&W Brand Colors
const AW_ORANGE = '#f97316';
const AW_BROWN = '#78350f';
const AW_COLORS = ['#f97316', '#78350f', '#fbbf24', '#ea580c', '#92400e', '#d97706', '#b45309'];

// Knowledge Banner Tips from A&W Profit Improvement Program & Manuals
const KNOWLEDGE_BANNER_TIPS = [
  // Sales & Forecasting
  { category: "Sales", icon: "📈", tip: "Sales Forecast Target: Less than 5% variance to actual results. Use Clearview's forecasting module for accuracy.", color: "from-green-500 to-emerald-600" },
  { category: "Sales", icon: "💵", tip: "Measure actual sales vs forecast DAILY. Adjust Level Calling and labour for the week based on variance.", color: "from-green-500 to-emerald-600" },
  { category: "Sales", icon: "🎯", tip: "Consider events, promotions, weather, holidays when forecasting. A 20% lift is typical for special events.", color: "from-green-500 to-emerald-600" },
  
  // Labor Management
  { category: "Labor", icon: "👥", tip: "Labor Target: +/- 0.5% variance to plan. 'The right staff on at the right time' is the golden rule.", color: "from-purple-500 to-violet-600" },
  { category: "Labor", icon: "⏰", tip: "Target Man Hours = Forecast Sales ÷ SPMH Target. Calculate daily for optimal scheduling.", color: "from-purple-500 to-violet-600" },
  { category: "Labor", icon: "📊", tip: "Adjust schedules before 11 AM daily. Proactive management beats reactive fixes every time.", color: "from-purple-500 to-violet-600" },
  { category: "Labor", icon: "🌙", tip: "Ensure closers leave at Midnight. Work with evening teams to ensure closing success.", color: "from-purple-500 to-violet-600" },
  
  // Food Cost Control
  { category: "Food Cost", icon: "🍟", tip: "Food Cost Variance Target: Less than 1.5%. Best-in-class restaurants achieve 1.0-1.2%!", color: "from-orange-500 to-amber-600" },
  { category: "Food Cost", icon: "📋", tip: "Conduct FULL Weekly Inventory counts on Sunday nights. One specialist = consistency.", color: "from-orange-500 to-amber-600" },
  { category: "Food Cost", icon: "⚖️", tip: "Weigh fries periodically for calibration. Staff should know proper portions for all items.", color: "from-orange-500 to-amber-600" },
  { category: "Food Cost", icon: "📝", tip: "Daily Level Calling Charts must be adjusted and printed DAILY based on sales forecast.", color: "from-orange-500 to-amber-600" },
  { category: "Food Cost", icon: "💡", tip: "Post the cost per item for high variance products. Educate staff about waste costs!", color: "from-orange-500 to-amber-600" },
  { category: "Food Cost", icon: "🎯", tip: "Achieving 1.2% variance = ~$8,000 additional profit in an average Freestanding restaurant!", color: "from-orange-500 to-amber-600" },
  
  // Service Excellence
  { category: "Service", icon: "🚗", tip: "Drive-Thru Target: 80 seconds. Counter Target: 150 seconds. Time every order!", color: "from-blue-500 to-cyan-600" },
  { category: "Service", icon: "⭐", tip: "Acknowledge guests within 5 seconds at speaker post. First impression matters!", color: "from-blue-500 to-cyan-600" },
  { category: "Service", icon: "🏃", tip: "Aces in Their Places - assign team members to roles matching their skills.", color: "from-blue-500 to-cyan-600" },
  
  // Inventory & Receiving
  { category: "Inventory", icon: "📦", tip: "Sign invoices ONLY after checking quantities match. Check product quality before accepting.", color: "from-teal-500 to-cyan-600" },
  { category: "Inventory", icon: "🔄", tip: "FIFO: First In, First Out. Rotate products properly to minimize waste and ensure freshness.", color: "from-teal-500 to-cyan-600" },
  { category: "Inventory", icon: "🌡️", tip: "Check product temp and condition before signing delivery. Reject items that don't meet specs.", color: "from-teal-500 to-cyan-600" },
  
  // Profit Principles
  { category: "Profit", icon: "💰", tip: "Prime Cost Target: 51% (Labor + Food Cost). Monitor weekly for maximum profitability.", color: "from-yellow-500 to-orange-600" },
  { category: "Profit", icon: "📈", tip: "Focus on fewer, more direct, weekly activities. Discipline in the program drives results.", color: "from-yellow-500 to-orange-600" },
  { category: "Profit", icon: "🤝", tip: "Hold weekly calls with management team - no longer than 30 minutes. Focus on 1-2 key actions.", color: "from-yellow-500 to-orange-600" },
  { category: "Profit", icon: "✅", tip: "Celebrate successes! Acknowledge staff input and reinforce ideas that improve results.", color: "from-yellow-500 to-orange-600" },
  
  // Food Safety & Quality
  { category: "Quality", icon: "🌡️", tip: "Maintain correct food temperatures. Check and record temps of all equipment daily.", color: "from-red-500 to-rose-600" },
  { category: "Quality", icon: "🍔", tip: "Never substitute ingredients without authorization. Consistency builds customer trust.", color: "from-red-500 to-rose-600" },
  { category: "Quality", icon: "⏱️", tip: "Hold products according to proper holding times. Quality degrades quickly!", color: "from-red-500 to-rose-600" },
  
  // Cleanliness
  { category: "Cleanliness", icon: "✨", tip: "Use 'FAST 5' - complete 5 quick tasks before rush. Clean as you go, not at the end.", color: "from-cyan-500 to-blue-600" },
  { category: "Cleanliness", icon: "🧹", tip: "Deep clean inside and out weekly. First impressions start in the parking lot!", color: "from-cyan-500 to-blue-600" }
];

// Daily Operational Tips (subset for tip of the day)
const OPERATIONAL_TIPS = KNOWLEDGE_BANNER_TIPS.slice(0, 12);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [storeReports, setStoreReports] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [todayChecklist, setTodayChecklist] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dailyTip, setDailyTip] = useState(null);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  const today = new Date().toISOString().split('T')[0];

  // Rotating banner effect - changes every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % KNOWLEDGE_BANNER_TIPS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchData();
    // Set random daily tip
    setDailyTip(OPERATIONAL_TIPS[Math.floor(Math.random() * OPERATIONAL_TIPS.length)]);
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      await axios.post(`${API_URL}/seed`);
      
      const storesRes = await axios.get(`${API_URL}/stores`);
      setStores(storesRes.data);

      const complianceRes = await axios.get(`${API_URL}/reports/compliance`);
      setCompliance(complianceRes.data);

      if (user?.role === 'owner') {
        const reportsRes = await axios.get(`${API_URL}/reports/stores`);
        setStoreReports(reportsRes.data);
      }

      const checklistsRes = await axios.get(`${API_URL}/checklists`, {
        params: { date: today }
      });
      if (checklistsRes.data.length > 0) {
        setTodayChecklist(checklistsRes.data[0]);
      }

      // Fetch recent posts for feed
      try {
        const postsRes = await axios.get(`${API_URL}/feed/posts`, { params: { limit: 3 } });
        setRecentPosts(postsRes.data);
      } catch (e) {
        // Feed may not be available for all roles
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startTodayChecklist = async () => {
    try {
      const templatesRes = await axios.get(`${API_URL}/templates`);
      if (templatesRes.data.length === 0) {
        toast.error('No checklist template found');
        return;
      }

      if (stores.length === 0) {
        toast.error('Please create a store first');
        navigate('/stores');
        return;
      }

      // Find Be Clean Daily or Daily Operations Excellence
      const dailyTemplate = templatesRes.data.find(t => 
        t.name.includes('Daily') || t.name.includes('Be Clean - Daily')
      ) || templatesRes.data[0];

      const checklistRes = await axios.post(`${API_URL}/checklists`, {
        store_id: stores[0].id,
        date: today,
        template_id: dailyTemplate.id
      });

      navigate(`/checklists/${checklistRes.data.id}`);
    } catch (error) {
      console.error('Error creating checklist:', error);
      toast.error('Failed to create checklist');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const complianceData = compliance ? [
    { name: 'Completed', value: compliance.completed_checklists },
    { name: 'Pending', value: compliance.total_checklists - compliance.completed_checklists }
  ] : [];

  // Transform store reports for chart
  const storeChartData = storeReports.map(store => ({
    name: store.store_name.split(' ')[0],
    compliance: store.compliance_rate,
    completed: store.completed_checklists,
    total: store.total_checklists
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <div className="text-slate-500">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header with brand photo */}
      <div className="relative rounded-2xl overflow-hidden shadow-lg grain" data-testid="dashboard-hero">
        <img src={BRAND_PHOTOS.counter} alt="Elmwood A&W restaurant counter" className="absolute inset-0 w-full h-full object-cover object-[center_40%] animate-ken-burns" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#3E2418]/95 via-[#3E2418]/80 to-[#3E2418]/35" />
        <div className="relative z-10 p-6 sm:p-8 text-white flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 reveal">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-orange-300 font-semibold">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight mt-2 flex items-center gap-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Star className="w-7 h-7 fill-amber-400 text-amber-400" />
              {user?.role === 'owner' ? '5 Star Operations Dashboard' : 'Daily Operations'}
            </h1>
            <p className="text-white/75 mt-2 text-sm sm:text-base">
              Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''} — {user?.role === 'owner' ? 'live view across all Elmwood restaurants.' : "here's what matters today."}
            </p>
          </div>
          
          {(user?.role === 'manager' || user?.role === 'supervisor') && (
            <Button
              onClick={todayChecklist ? () => navigate(`/checklists/${todayChecklist.id}`) : startTodayChecklist}
              className="bg-[#F26522] hover:bg-[#d9551a] text-white rounded-full font-semibold shadow-lg shadow-orange-500/30 btn-press"
              data-testid="start-checklist-btn"
            >
              {todayChecklist ? (
                <>
                  Continue Today&apos;s Checklist
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Start Daily Checklist
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Rotating Knowledge Banner */}
      <div 
        className="bg-white border border-orange-200/70 rounded-2xl p-4 text-[#3E2418] shadow-sm transition-all duration-500 ease-in-out" 
        data-testid="knowledge-banner"
      >
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center text-2xl flex-shrink-0">{KNOWLEDGE_BANNER_TIPS[currentBannerIndex]?.icon}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Lightbulb className="w-4 h-4 text-[#F26522]" />
              <span className="text-xs font-semibold uppercase tracking-wider text-[#F26522]">
                {KNOWLEDGE_BANNER_TIPS[currentBannerIndex]?.category} Tip
              </span>
            </div>
            <p className="text-sm md:text-base font-medium leading-snug">
              {KNOWLEDGE_BANNER_TIPS[currentBannerIndex]?.tip}
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-1">
            {KNOWLEDGE_BANNER_TIPS.slice(0, 8).map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentBannerIndex(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentBannerIndex % 8 ? 'bg-[#F26522] scale-125' : 'bg-orange-200 hover:bg-orange-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Daily Tip Card */}
      {dailyTip && (
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-orange-200 rounded-sm" data-testid="daily-tip">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-2xl flex-shrink-0">
                {dailyTip.icon}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Lightbulb className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-semibold text-orange-600 uppercase tracking-wide">
                    Tip of the Day - {dailyTip.category}
                  </span>
                </div>
                <p className="text-slate-700">{dailyTip.tip}</p>
                <p className="text-xs text-slate-500 mt-2">Source: {dailyTip.source}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-200 rounded-sm" data-testid="stat-total">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Checklists</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{compliance?.total_checklists || 0}</p>
              </div>
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm" data-testid="stat-completed">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Completed</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{compliance?.completed_checklists || 0}</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm" data-testid="stat-pending">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Pending</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">
                  {(compliance?.total_checklists || 0) - (compliance?.completed_checklists || 0)}
                </p>
              </div>
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm" data-testid="stat-compliance">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">5 Star Score</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">{compliance?.compliance_rate || 0}%</p>
              </div>
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <Star className="w-5 h-5 text-orange-600 fill-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Store Performance Chart (for owners) */}
        {user?.role === 'owner' && storeReports.length > 0 && (
          <Card className="lg:col-span-2 bg-white border border-slate-200 rounded-sm" data-testid="store-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <Building2 className="w-5 h-5 text-orange-600" />
                Store Performance (7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={storeChartData}>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip 
                      formatter={(value, name) => [
                        name === 'compliance' ? `${value}%` : value,
                        name === 'compliance' ? 'Compliance Rate' : name
                      ]}
                    />
                    <Bar dataKey="compliance" fill={AW_ORANGE} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Category Performance */}
        <Card className={`bg-white border border-slate-200 rounded-sm ${user?.role !== 'owner' || storeReports.length === 0 ? 'lg:col-span-2' : ''}`} data-testid="category-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Target className="w-5 h-5 text-orange-600" />
              Category Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {compliance?.category_breakdown?.slice(0, 6).map((category, index) => (
                <div key={category.category} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-700 font-medium truncate">{category.category}</span>
                    <span className={`font-bold ${category.rate >= 80 ? 'text-green-600' : category.rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                      {category.rate}%
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ 
                        width: `${category.rate}%`,
                        backgroundColor: AW_COLORS[index % AW_COLORS.length]
                      }}
                    />
                  </div>
                </div>
              ))}
              {(!compliance?.category_breakdown || compliance.category_breakdown.length === 0) && (
                <div className="text-center py-8 text-slate-500">
                  <ClipboardCheck className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">Complete checklists to see performance</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Compliance Donut */}
        <Card className="bg-white border border-slate-200 rounded-sm" data-testid="compliance-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Award className="w-5 h-5 text-orange-600" />
              Compliance Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={complianceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={65}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    <Cell fill={AW_ORANGE} />
                    <Cell fill="#e2e8f0" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: AW_ORANGE }} />
                <span className="text-sm text-slate-600">Completed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-slate-200 rounded-full" />
                <span className="text-sm text-slate-600">Pending</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Feed Section */}
      <Card className="bg-white border border-slate-200 rounded-sm" data-testid="feed-section">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Users className="w-5 h-5 text-orange-600" />
              Team Feed
            </CardTitle>
            <Link to="/feed">
              <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-700">
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {recentPosts.length > 0 ? (
            <div className="space-y-4">
              {recentPosts.map((post) => (
                <div key={post.id} className="flex gap-3 p-3 bg-slate-50 rounded-sm">
                  <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
                    {post.author_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{post.author_name}</span>
                      {post.store_name && (
                        <span className="text-xs text-slate-500">@ {post.store_name}</span>
                      )}
                      <span className="text-xs text-slate-400">{formatDate(post.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-700 mt-1 line-clamp-2">{post.content}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Heart className={`w-3 h-3 ${post.likes?.length > 0 ? 'fill-red-500 text-red-500' : ''}`} />
                        {post.likes?.length || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" />
                        {post.comments?.length || 0}
                      </span>
                    </div>
                  </div>
                  {post.image_url && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${post.image_url}`}
                      alt="Post"
                      className="w-16 h-16 object-cover rounded-sm flex-shrink-0"
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-500">No posts yet. Share updates with your team!</p>
              <Link to="/feed">
                <Button variant="outline" size="sm" className="mt-3 text-orange-600 border-orange-300 hover:bg-orange-50">
                  <Plus className="w-4 h-4 mr-1" />
                  Create First Post
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/checklists" className="block">
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-sm hover:from-orange-600 hover:to-orange-700 transition-all cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <ClipboardCheck className="w-8 h-8" />
              <div>
                <p className="font-bold">Checklists</p>
                <p className="text-xs text-orange-100">Daily & Weekly</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/resources" className="block">
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-sm hover:from-amber-600 hover:to-amber-700 transition-all cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <BookOpen className="w-8 h-8" />
              <div>
                <p className="font-bold">Resources</p>
                <p className="text-xs text-amber-100">Guides & Manuals</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/reports" className="block">
          <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-sm hover:from-yellow-600 hover:to-yellow-700 transition-all cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <TrendingUp className="w-8 h-8" />
              <div>
                <p className="font-bold">Reports</p>
                <p className="text-xs text-yellow-100">Analytics</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/tips" className="block">
          <Card className="bg-gradient-to-br from-orange-600 to-amber-600 text-white rounded-sm hover:from-orange-700 hover:to-amber-700 transition-all cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <Lightbulb className="w-8 h-8" />
              <div>
                <p className="font-bold">Tips</p>
                <p className="text-xs text-orange-100">Best Practices</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Owner Quick Actions */}
      {user?.role === 'owner' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="owner-shortcuts">
          <Link to="/kpi" className="block">
            <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-sm hover:from-green-600 hover:to-emerald-700 transition-all cursor-pointer">
              <CardContent className="p-4 flex items-center gap-3">
                <Target className="w-8 h-8" />
                <div>
                  <p className="font-bold">KPI Dashboard</p>
                  <p className="text-xs text-green-100">View Metrics</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/profit-loss" className="block">
            <Card className="bg-gradient-to-br from-purple-500 to-violet-600 text-white rounded-sm hover:from-purple-600 hover:to-violet-700 transition-all cursor-pointer">
              <CardContent className="p-4 flex items-center gap-3">
                <Calculator className="w-8 h-8" />
                <div>
                  <p className="font-bold">P&L Report</p>
                  <p className="text-xs text-purple-100">Financials</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/yearly-summary" className="block">
            <Card className="bg-gradient-to-br from-amber-500 to-yellow-600 text-white rounded-sm hover:from-amber-600 hover:to-yellow-700 transition-all cursor-pointer">
              <CardContent className="p-4 flex items-center gap-3">
                <Trophy className="w-8 h-8" />
                <div>
                  <p className="font-bold">Year Review</p>
                  <p className="text-xs text-amber-100">Download & Share</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      )}

      {/* Empty state for new owners */}
      {stores.length === 0 && user?.role === 'owner' && (
        <Card className="bg-white border-2 border-dashed border-orange-300 rounded-sm" data-testid="empty-state">
          <CardContent className="py-12 text-center">
            <img
              src="https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/215jxmio_Corp_ArrowLogo_Bear_2C_HL_EN_Lg.png"
              alt="A&W Logo"
              className="w-32 h-32 object-contain mx-auto mb-6"
            />
            <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Welcome to Elmwood Management
            </h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              Get started by adding your first A&W store location. Then assign daily checklists and track 5 Star compliance.
            </p>
            <Button
              onClick={() => navigate('/stores')}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Store
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
